/**
 * Single source of truth for the dates derived from a plan/client-plan's
 * effective date: End Date (1 year minus 1 day), Renewal Date (exactly 1
 * year), Available Start Date (3 months before), Available End Date (same
 * as End Date).
 *
 * Previously this math was duplicated in three places (PlanOverview.tsx's
 * plan-level validation, its per-client coverage display, and
 * useClientPlans.ts's per-client Available Date derivation) - any of the
 * three could silently drift from the others. Extracted here so all three
 * compute identically and the math is unit-testable on its own.
 *
 * Uses plain calendar-integer arithmetic (Date.UTC), never a mutated local
 * Date - constructing `new Date("2025-11-01")` parses as UTC midnight, and
 * mutating it with local setters (setMonth/setFullYear) can shift the
 * calendar date by a day depending on the runtime's timezone. This can't
 * drift regardless of where it runs (server or a browser in any timezone).
 */

export interface CoverageDates {
  endDate: string | null;
  renewalDate: string | null;
  availableStartDate: string | null;
  availableEndDate: string | null;
}

interface CalendarDate {
  year: number;
  month: number; // 0-indexed, matches Date
  day: number;
}

function extractCalendarDate(input: string | Date): CalendarDate | null {
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return null;
    // A Date object is assumed to already represent the intended calendar
    // date in local time (e.g. built by parseDateLocal) - read it back with
    // local getters, not UTC ones.
    return { year: input.getFullYear(), month: input.getMonth(), day: input.getDate() };
  }
  // Date-only string ("2025-11-01"): read the calendar digits directly,
  // never through `new Date(...)`, so no timezone is involved at all.
  const dateOnly = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return { year: Number(dateOnly[1]), month: Number(dateOnly[2]) - 1, day: Number(dateOnly[3]) };
  }
  // Anything else (e.g. a full ISO timestamp with a non-midnight time):
  // parse it, then read back via UTC getters - `new Date(str)` always
  // treats the string as UTC, so UTC getters are what recovers the exact
  // calendar date the string encoded, regardless of the runtime's timezone.
  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) return null;
  return { year: parsed.getUTCFullYear(), month: parsed.getUTCMonth(), day: parsed.getUTCDate() };
}

/** Adds `monthsDelta` months then `daysDelta` days, calendar-correct (month/day overflow rolls over, matching Date's own normalization). */
function addToCalendarDate(date: CalendarDate, monthsDelta: number, daysDelta = 0): Date {
  const result = new Date(Date.UTC(date.year, date.month + monthsDelta, date.day));
  if (daysDelta) result.setUTCDate(result.getUTCDate() + daysDelta);
  return result;
}

/** Returns null for every field when `effectiveDate` is missing/invalid. */
export function deriveCoverageDates(
  effectiveDate: string | Date | null | undefined,
): CoverageDates {
  const none: CoverageDates = {
    endDate: null,
    renewalDate: null,
    availableStartDate: null,
    availableEndDate: null,
  };
  if (!effectiveDate) return none;

  const calendarDate = extractCalendarDate(effectiveDate);
  if (!calendarDate) return none;

  const endDate = addToCalendarDate(calendarDate, 12, -1);
  const renewalDate = addToCalendarDate(calendarDate, 12);
  const availableStartDate = addToCalendarDate(calendarDate, -3);

  return {
    endDate: endDate.toISOString(),
    renewalDate: renewalDate.toISOString(),
    availableStartDate: availableStartDate.toISOString(),
    availableEndDate: endDate.toISOString(),
  };
}
